/**
 * OnboardingChecklist — first-run welcome on the dashboard.
 *
 * Persona-aware 3-step checklist that appears under the greeting
 * card for users who are still "new" (defined per persona below).
 * Each step links to the action. Dismissible — once dismissed the
 * checklist stays gone forever (writes to `dismissed_onboarding`
 * in localStorage).
 *
 * Per-persona heuristics:
 *   breeder      — show until ≥1 dog has photos AND a marketplace
 *                  listing OR the user explicitly dismisses
 *   transporter  — show until Stripe payouts enabled + first bid
 *   buyer        — show until first watchlist entry OR inquiry
 *   dog_owner    — show until first care_log entry
 *
 * Drives activation: studies show a visible 3-step checklist on
 * day-1 lifts activation by ~30%. Cheap because we have all the
 * signals already from `/me/nav-context`.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { CheckCircleIcon as CheckCircle, CircleIcon as Circle, SparkleIcon as Sparkle, XIcon as X, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { readPersonaOverride } from "@/components/PersonaSwitcher";

const STORAGE_KEY = "dismissed_onboarding_v1";

// Each step: id, label, hint, route, and a `done(signals, extras)`
// predicate that decides whether to render with a green check.
const STEPS = {
  breeder: [
    {
      id: "first-dog",
      label: "Register your first dog",
      hint: "Photos + breed + DOB. Takes ~2 minutes.",
      to: "/dogs/new",
      done: (s) => (s.dogs_count || 0) > 0,
    },
    {
      id: "kennel-page",
      label: "Set up your kennel storefront",
      hint: "About + photos + verified-breeder badge.",
      to: "/me/store",
      done: (s) => !!s.has_breeder_row,
    },
    {
      id: "first-listing",
      label: "List a dog for sale",
      hint: "Or list a litter. Lands in front of every buyer browsing.",
      to: "/marketplace",
      done: (_s, extras) => (extras?.marketplace_listings || 0) > 0,
    },
  ],
  buyer: [
    {
      id: "preferences",
      label: "Tell us what you're looking for",
      hint: "Breed, size, location. We'll alert you when matching dogs are listed.",
      to: "/buyer-preferences",
      done: (_s, extras) => !!extras?.has_buyer_preferences,
    },
    {
      id: "watchlist",
      label: "Save a breeder or litter you like",
      hint: "Heart icon on any kennel page or litter card.",
      to: "/marketplace",
      done: (_s, extras) => (extras?.watchlist_count || 0) > 0,
    },
    {
      id: "first-inquiry",
      label: "Send your first inquiry",
      hint: "Ask a breeder about their dogs — they typically reply within 24h.",
      to: "/marketplace",
      done: (_s, extras) => (extras?.inquiries_made || 0) > 0,
    },
  ],
  transporter: [
    {
      id: "stripe-connect",
      label: "Connect your Stripe payouts",
      hint: "Required before you can take bids. Takes 5 minutes.",
      to: "/transport/apply",
      done: (s) => !!s.is_transporter && !s.is_transporter_pending,
    },
    {
      id: "service-area",
      label: "Set your service area",
      hint: "Which states do you cover? Bid invitations only route to matches.",
      to: "/transporter",
      done: (_s, extras) => (extras?.service_area_states_count || 0) > 0,
    },
    {
      id: "first-bid",
      label: "Submit your first bid",
      hint: "Open bid invitations appear on your dashboard. Quote a price and you're off.",
      to: "/transporter?focus=bids",
      done: (_s, extras) => (extras?.bids_submitted || 0) > 0,
    },
  ],
  dog_owner: [
    {
      id: "first-log",
      label: "Log your first meal or walk",
      hint: "Tap the + button at the bottom of any screen on mobile.",
      to: "/dog-log",
      done: (_s, extras) => (extras?.care_log_count || 0) > 0,
    },
    {
      id: "vet-contact",
      label: "Add your vet's contact",
      hint: "Saves time when a sitter needs to call them in an emergency.",
      to: "/dog-log",
      done: (_s, extras) => (extras?.has_vet_contact || 0) > 0,
    },
    {
      id: "share-sitter",
      label: "Generate a sitter link",
      hint: "A read-only view of your dog's recent care — perfect for boarding.",
      to: "/dog-log",
      done: (_s, extras) => (extras?.has_sitter_link || 0) > 0,
    },
  ],
  service_provider: [
    {
      id: "provider-apply",
      label: "Submit your provider application",
      hint: "Tell us what you offer — grooming, training, walking, sitting, boarding.",
      to: "/become-a-provider",
      done: (_s, extras) => !!extras?.has_provider_row,
    },
    {
      id: "provider-services",
      label: "Add your services & prices",
      hint: "Clients book and pay for exactly what you list — be specific.",
      to: "/become-a-provider",
      done: (_s, extras) => (extras?.services_count || 0) > 0,
    },
    {
      id: "provider-payouts",
      label: "Connect payouts with Stripe",
      hint: "So booking payments land straight in your bank account.",
      to: "/become-a-provider",
      done: (_s, extras) => !!extras?.payouts_enabled,
    },
  ],
  storefront_seller: [
    {
      id: "store-open",
      label: "Open your storefront",
      hint: "Name, story, and photos — your shop's public page.",
      to: "/me/store",
      done: (s) => !!s.is_storefront_seller,
    },
    {
      id: "store-product",
      label: "Add your first product",
      hint: "A photo and a price is all it takes to start selling.",
      to: "/me/store?tab=products",
      done: (_s, extras) => (extras?.products_count || 0) > 0,
    },
    {
      id: "store-payouts",
      label: "Connect payouts with Stripe",
      hint: "So order payments land straight in your bank account.",
      to: "/me/store",
      done: (_s, extras) => !!extras?.payouts_enabled,
    },
  ],
};

const PERSONA_GREETING = {
  breeder: "Welcome to your kennel",
  buyer: "Welcome — let's find your dog",
  transporter: "Welcome to Armstrong Transport",
  dog_owner: "Welcome to Dog Log",
  service_provider: "Welcome — let's get you booked",
  storefront_seller: "Welcome — let's open your shop",
};

export default function OnboardingChecklist() {
  const [ctx, setCtx] = useState(null);
  const [override, setOverride] = useState(() => readPersonaOverride());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
    } catch {
      return new Set();
    }
  });
  // Side-channel data the resolver doesn't already return — fetched
  // lazily and shaped per persona. Keep network cost predictable:
  // one extra GET per persona, only on first render.
  const [extras, setExtras] = useState({});
  // Server-side manual overrides: step IDs the user (or an admin)
  // has explicitly marked complete via /api/onboarding/steps. Merged
  // with the auto-graded signals when deciding the final state.
  const [manualOverrides, setManualOverrides] = useState(new Set());
  const [marking, setMarking] = useState(null); // step id currently saving

  useEffect(() => {
    let cancelled = false;
    api.get("/me/nav-context", { silent: true })
      .then((r) => { if (!cancelled) setCtx(r.data || null); })
      .catch(() => { if (!cancelled) setCtx(null); });
    api.get("/onboarding/steps", { silent: true })
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data?.overrides) || [];
        // Only care about welcome-checklist overrides here.
        setManualOverrides(new Set(
          rows.filter((o) => o.guide === "welcome").map((o) => o.step_id),
        ));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onChange = (e) => setOverride(e?.detail?.persona || null);
    window.addEventListener("persona-override-changed", onChange);
    return () => window.removeEventListener("persona-override-changed", onChange);
  }, []);

  // Fetch persona-specific extras when persona resolves.
  useEffect(() => {
    if (!ctx) return;
    const overrideValid = override && (ctx.available || []).includes(override);
    const persona = overrideValid ? override : ctx.primary;
    if (persona === "buyer") {
      api.get("/dashboard/stats", { silent: true })
        .then((r) => setExtras({
          watchlist_count:
            (r.data?.watching_breeders?.length || 0) +
            (r.data?.watching_litters?.length || 0),
        }))
        .catch(() => {});
    }
    if (persona === "service_provider") {
      Promise.all([
        api.get("/providers/mine", { silent: true }).catch(() => ({ data: null })),
        api.get("/providers/mine/services", { silent: true }).catch(() => ({ data: [] })),
        api.get("/connect/account", { silent: true }).catch(() => ({ data: {} })),
      ]).then(([p, s, c]) => {
        const services = Array.isArray(s.data) ? s.data : (s.data?.services || []);
        setExtras({
          has_provider_row: !!(p.data && (p.data.id || p.data.provider?.id)),
          services_count: services.length,
          payouts_enabled: !!c.data?.account?.payouts_enabled,
        });
      });
    }
    if (persona === "storefront_seller") {
      Promise.all([
        api.get("/me/store/products", { silent: true }).catch(() => ({ data: {} })),
        api.get("/connect/account", { silent: true }).catch(() => ({ data: {} })),
      ]).then(([pr, c]) => {
        const products = Array.isArray(pr.data) ? pr.data : (pr.data?.products || []);
        setExtras({
          products_count: products.length,
          payouts_enabled: !!c.data?.account?.payouts_enabled,
        });
      });
    }
    // For other personas the signals on `nav-context` are enough
    // to grade steps 1-2; step-3 stays open by default until the
    // user does it. We could load `care_log_count` etc. but the
    // checklist is intentionally shy on network calls.
  }, [ctx, override]);

  // PostHog activation funnel: one per-persona snapshot per dashboard
  // visit — powers the "where does each persona stall?" report.
  useEffect(() => {
    if (!ctx) return;
    const p = (override && (ctx.available || []).includes(override)) ? override : ctx.primary;
    const defs = STEPS[p];
    if (!defs) return;
    const sig = ctx.signals || {};
    let done = 0;
    for (const s of defs) {
      try { if (s.done(sig, extras)) done += 1; } catch { /* ignore */ }
    }
    track("onboarding_checklist_state", {
      persona: p, done, total: defs.length, completed: done === defs.length,
    });
  }, [ctx, override, extras]);

  if (!ctx) return null;
  const overrideValid = override && (ctx.available || []).includes(override);
  const persona = overrideValid ? override : ctx.primary;
  if (dismissed.has(persona)) return null;

  const steps = STEPS[persona];
  if (!steps) return null;

  const signals = ctx.signals || {};
  // A step is "complete" if either the auto-detection trips OR the
  // user (/admin) manually marked it done. Either path is enough.
  const graded = steps.map((s) => ({
    ...s,
    completed: manualOverrides.has(s.id) || !!s.done(signals, extras),
    manually_done: manualOverrides.has(s.id),
  }));
  const doneCount = graded.filter((s) => s.completed).length;

  // Auto-hide when ALL steps complete — we don't want to nag.
  if (doneCount === steps.length) return null;

  const dismiss = () => {
    const next = new Set(dismissed); next.add(persona);
    setDismissed(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
    } catch { /* private mode */ }
  };

  const markDone = async (stepId) => {
    setMarking(stepId);
    try {
      await api.post("/onboarding/steps", { guide: "welcome", step_id: stepId });
      setManualOverrides((prev) => {
        const next = new Set(prev); next.add(stepId); return next;
      });
    } catch { /* swallow — UI will look idle, retry on next click */ }
    finally { setMarking(null); }
  };

  const unmarkDone = async (stepId) => {
    setMarking(stepId);
    try {
      await api.delete(`/onboarding/steps/welcome/${stepId}`);
      setManualOverrides((prev) => {
        const next = new Set(prev); next.delete(stepId); return next;
      });
    } catch { /* swallow */ }
    finally { setMarking(null); }
  };

  return (
    <div
      className="mb-6 rounded-2xl border border-gold/30 bg-gradient-to-br from-[#FBF7EC] to-white p-5 sm:p-6 relative"
      data-testid="onboarding-checklist"
      data-persona={persona}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss checklist"
        data-testid="onboarding-dismiss"
        className="absolute top-3 right-3 inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-[#FAFAFA] text-[#6B6B6B] hover:text-ink transition-colors"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-3 mb-4 pr-7">
        <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-ink text-gold flex-shrink-0">
          <Sparkle size={18} weight="duotone" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-1">
            Get started · {doneCount}/{steps.length} complete
          </p>
          <h2 className="font-spectral text-2xl tracking-tight leading-tight">
            {PERSONA_GREETING[persona] || "Welcome"}
          </h2>
        </div>
        {/* Bulk "Mark all done" — handy when the user (often the admin)
            has already done all this offline or via a different surface
            and just wants the checklist to stop nagging. */}
        {doneCount < steps.length && (
          <button
            type="button"
            onClick={async () => {
              const open = graded.filter((s) => !s.completed);
              await Promise.all(
                open.map((s) =>
                  api.post("/onboarding/steps", { guide: "welcome", step_id: s.id }).catch(() => null)
                )
              );
              setManualOverrides((prev) => {
                const next = new Set(prev);
                open.forEach((s) => next.add(s.id));
                return next;
              });
            }}
            data-testid="onboarding-skip-all"
            className="hidden sm:inline-flex text-[10px] font-semibold text-[#6B6B6B] hover:text-ink uppercase tracking-wider px-3 py-1.5 rounded-full hover:bg-white/60 mr-8 flex-shrink-0"
            title="Mark every step complete"
          >
            Skip all
          </button>
        )}
      </div>

      <div className="space-y-2">
        {graded.map((s) => (
          <div
            key={s.id}
            data-testid={`onboarding-step-${s.id}`}
            className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors group ${
              s.completed
                ? "border-[#DCFCE7] bg-[#F0FDF4] opacity-75"
                : "border-[#E5E5E5] bg-white hover:border-ink/40"
            }`}
          >
            <span className="flex-shrink-0">
              {s.completed ? (
                <CheckCircle size={20} weight="fill" className="text-[#15803D]" />
              ) : (
                <Circle size={20} weight="duotone" className="text-[#9CA3AF]" />
              )}
            </span>
            <Link
              to={s.to}
              className="flex-1 min-w-0 group/link"
              data-testid={`onboarding-step-link-${s.id}`}
            >
              <span className={`block text-[13px] font-semibold ${s.completed ? "line-through text-[#6B6B6B]" : "text-ink"}`}>
                {s.label}
                {s.manually_done && (
                  <span className="ml-2 text-[10px] font-normal text-[#15803D] uppercase tracking-wider">· marked done</span>
                )}
              </span>
              <span className="block text-[11px] text-[#6B6B6B] mt-0.5">{s.hint}</span>
            </Link>

            {/* Manual override toggle — quick "mark done" / "undo" so
                the user (or an admin) can clear a step the auto
                detector hasn't tripped yet. */}
            {s.completed && s.manually_done ? (
              <button
                type="button"
                onClick={() => unmarkDone(s.id)}
                disabled={marking === s.id}
                data-testid={`onboarding-undo-${s.id}`}
                className="text-[10px] font-semibold text-[#6B6B6B] hover:text-ink uppercase tracking-wider px-2 py-1 rounded-full hover:bg-white/60 flex-shrink-0 disabled:opacity-50"
                title="Un-mark this step (re-enables auto detection)"
              >
                Undo
              </button>
            ) : !s.completed ? (
              <>
                <button
                  type="button"
                  onClick={() => markDone(s.id)}
                  disabled={marking === s.id}
                  data-testid={`onboarding-markdone-${s.id}`}
                  className="text-[10px] font-semibold text-[#6B6B6B] hover:text-[#15803D] uppercase tracking-wider px-2 py-1 rounded-full hover:bg-[#DCFCE7] flex-shrink-0 disabled:opacity-50"
                  title="Skip the action — mark this step as complete"
                >
                  Mark done
                </button>
                <ArrowRight size={12} weight="bold" className="text-[#9CA3AF] group-hover:text-ink transition-colors flex-shrink-0" />
              </>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
